import React from 'react';
import { getInitials, getAvatarColor, resolveFileUrl } from '../../utils/constants';
import {
  ArrowDownIcon, MinusIcon, ArrowUpIcon, BoltIcon,
  HomeIcon, ClipboardDocumentListIcon, BellIcon, UsersIcon,
  BuildingOfficeIcon, UserGroupIcon, ChartBarIcon, ClockIcon,
  MegaphoneIcon, LockOpenIcon, ArchiveBoxIcon, Cog6ToothIcon,
  TrophyIcon, CalendarDaysIcon, CreditCardIcon, BuildingOffice2Icon,
  ExclamationTriangleIcon, ChatBubbleLeftIcon, LockClosedIcon,
  DocumentChartBarIcon, CheckCircleIcon, FingerPrintIcon, ListBulletIcon,
  SunIcon, ArrowLeftOnRectangleIcon, ArrowRightOnRectangleIcon, CakeIcon,
  PlayIcon, StopIcon, PauseIcon, FireIcon, SparklesIcon, ArrowPathIcon,
  ShieldCheckIcon, DocumentTextIcon, ReceiptRefundIcon, PhoneIcon,
} from '@heroicons/react/24/outline';

// ... (other components unchanged) ...

// ─── NavIcon ─────────────────────────────────────────────────────────────────
const NAV_ICONS = {
  HomeIcon, ClipboardDocumentListIcon, BellIcon, UsersIcon,
  BuildingOfficeIcon, UserGroupIcon, ChartBarIcon, ClockIcon,
  MegaphoneIcon, LockOpenIcon, ArchiveBoxIcon, Cog6ToothIcon,
  TrophyIcon, CalendarDaysIcon, CreditCardIcon, BuildingOffice2Icon,
  ExclamationTriangleIcon, ChatBubbleLeftIcon, LockClosedIcon,
  DocumentChartBarIcon, CheckCircleIcon, FingerPrintIcon, ListBulletIcon,
  SunIcon, ArrowLeftOnRectangleIcon, ArrowRightOnRectangleIcon, CakeIcon,
  PlayIcon, StopIcon, PauseIcon, FireIcon, SparklesIcon, ArrowPathIcon,
  ShieldCheckIcon, DocumentTextIcon, ReceiptRefundIcon, PhoneIcon,
};
export const NavIcon = ({ name, className = 'w-5 h-5' }) => {
  const Icon = NAV_ICONS[name];
  if (!Icon) return null;
  return <Icon className={className} />;
};

// ─── Avatar ────────────────────────────────────────────────────────────────────
export const Avatar = ({ name = '', src = null, size = 'md', className = '' }) => {
  const sizes = { xs:'w-6 h-6 text-xs', sm:'w-8 h-8 text-xs', md:'w-9 h-9 text-sm', lg:'w-11 h-11 text-base', xl:'w-16 h-16 text-xl' };
  const resolved = resolveFileUrl(src);
  if (resolved) return <img src={resolved} alt={name} className={`${sizes[size]} rounded-full object-cover flex-shrink-0 ring-2 ring-white dark:ring-slate-800 ${className}`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
  return (
    <div className={`${sizes[size]} ${getAvatarColor(name)} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${className}`}>
      {getInitials(name)}
    </div>
  );
};

export const AvatarGroup = ({ users = [], max = 3 }) => {
  const shown = users.slice(0, max), rest = users.length - max;
  return (
    <div className="flex -space-x-2">
      {shown.map((u, i) => <div key={u._id||i} title={u.name} className="ring-2 ring-white dark:ring-slate-800 rounded-full"><Avatar name={u.name} src={u.avatar} size="sm" /></div>)}
      {rest > 0 && <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 ring-2 ring-white dark:ring-slate-800 flex items-center justify-center text-xs text-slate-600 dark:text-slate-300 font-medium">+{rest}</div>}
    </div>
  );
};

// ─── Badge ─────────────────────────────────────────────────────────────────────
export const Badge = ({ children, text, variant, className = '', dot }) => {
  const label = children ?? text;
  const variants = {
    primary:   'bg-brand-100 text-brand-700 dark:bg-brand-950/40 dark:text-brand-400',
    secondary: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    success:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    warning:   'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
    danger:    'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
    info:      'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  };
  const variantClass = variant ? (variants[variant] || '') : '';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${variantClass} ${className}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
      {label}
    </span>
  );
};

// ─── Button ────────────────────────────────────────────────────────────────────
export const Button = ({ children, variant='primary', size='md', className='', loading, ...props }) => {
  const variants = {
    primary:   'bg-brand-600 hover:bg-brand-700 text-white shadow-sm glow-button hover:shadow-brand-500/20',
    secondary: 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-205 glow-button',
    danger:    'bg-red-600 hover:bg-red-700 text-white glow-button hover:shadow-red-500/20',
    ghost:     'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95 transition-all',
    success:   'bg-emerald-600 hover:bg-emerald-700 text-white glow-button hover:shadow-emerald-500/20',
  };
  const sizes = { sm:'px-3 py-1.5 text-xs', md:'px-4 py-2 text-sm', lg:'px-5 py-2.5 text-base' };
  return (
    <button className={`inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={loading || props.disabled} {...props}>
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({ size='md' }) => {
  const sizes = { sm:'w-4 h-4', md:'w-6 h-6', lg:'w-8 h-8' };
  return (
    <svg className={`animate-spin ${sizes[size]} text-current`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
    </svg>
  );
};

// ─── Modal ─────────────────────────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, size='md', bodyClassName = 'p-6' }) => {
  if (!open) return null;
  const sizes = { sm:'max-w-md', md:'max-w-xl', lg:'max-w-2xl', xl:'max-w-4xl' };
  const hasCustomScroll = bodyClassName && (bodyClassName.includes('overflow-') || bodyClassName.includes('p-0'));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md transition-opacity" onClick={onClose}/>
      <div className={`relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full ${sizes[size]} animate-slide-up max-h-[90vh] flex flex-col border border-slate-200/80 dark:border-slate-800/80 overflow-hidden`}>
        {title ? (
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
            <h2 className="text-lg font-extrabold font-display text-slate-900 dark:text-white tracking-tight">{title}</h2>
            <button onClick={onClose} aria-label="Close modal" className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-all flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        ) : null}
        <div className={`flex-1 flex flex-col min-h-0 ${hasCustomScroll ? '' : 'overflow-y-auto'} ${bodyClassName}`}>{children}</div>
      </div>
    </div>
  );
};

// ─── Input ────────────────────────────────────────────────────────────────────
export const Input = ({ label, error, hint, className='', ...props }) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>}
    <input className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-all outline-none bg-white/80 dark:bg-slate-950/80 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 ${error ? 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-4 focus:ring-red-100 dark:focus:ring-red-950/20' : 'border-slate-200 dark:border-slate-800 focus:border-brand-500 focus:ring-4 focus:ring-brand-100 dark:focus:ring-brand-950/20'}`} {...props}/>
    {hint  && <p className="text-xs text-slate-400 dark:text-slate-500">{hint}</p>}
    {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
  </div>
);

// ─── Select ────────────────────────────────────────────────────────────────────
export const Select = ({ label, error, children, className='', ...props }) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>}
    <select className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-all outline-none bg-white/80 dark:bg-slate-950/80 text-slate-900 dark:text-white ${error ? 'border-red-300 dark:border-red-700 focus:border-red-500' : 'border-slate-200 dark:border-slate-800 focus:border-brand-500 focus:ring-4 focus:ring-brand-100 dark:focus:ring-brand-950/20'}`} {...props}>
      {children}
    </select>
    {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
  </div>
);

// ─── Textarea ─────────────────────────────────────────────────────────────────
export const Textarea = ({ label, error, className='', ...props }) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>}
    <textarea rows={3} className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-all outline-none resize-none bg-white/80 dark:bg-slate-950/80 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 ${error ? 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-4 focus:ring-brand-100 dark:focus:ring-brand-950/20' : 'border-slate-200 dark:border-slate-800 focus:border-brand-500 focus:ring-4 focus:ring-brand-100 dark:focus:ring-brand-950/20'}`} {...props}/>
    {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
  </div>
);

// ─── Card ─────────────────────────────────────────────────────────────────────
export const Card = ({ children, className='', onClick }) => (
  <div className={`premium-glass-card ${onClick ? 'cursor-pointer' : ''} ${className}`} onClick={onClick}>
    {children}
  </div>
);

// ─── EmptyState ───────────────────────────────────────────────────────────────
export const EmptyState = ({ icon, title, description, action }) => {
  const isElement = React.isValidElement(icon);
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4">{isElement ? icon : <span className="text-5xl">{icon}</span>}</div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{title}</h3>
      {description && <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-sm">{description}</p>}
      {action}
    </div>
  );
};

// ─── PriorityIcon ────────────────────────────────────────────────────────────
const PRIORITY_ICONS = {
  ArrowDownIcon, MinusIcon, ArrowUpIcon, BoltIcon,
};
export const PriorityIcon = ({ iconName, className = 'w-3 h-3 inline-block' }) => {
  const Icon = PRIORITY_ICONS[iconName];
  if (!Icon) return null;
  return <Icon className={className} />;
};

// ─── ConfirmDialog ────────────────────────────────────────────────────────────
export const ConfirmDialog = ({ open, onClose, onConfirm, title, message, confirmText = 'Confirm', confirmVariant = 'primary', loading }) => (
  <Modal open={open} onClose={onClose} title={title} size="sm">
    <div className="space-y-6 flex-1">
      <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 leading-relaxed">{message}</p>
      <div className="flex gap-3 justify-end pt-2">
        <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant={confirmVariant} onClick={onConfirm} loading={loading}>{confirmText}</Button>
      </div>
    </div>
  </Modal>
);

// ─── ReasonModal ─────────────────────────────────────────────────────────────
// Used for blocked reason + deadline reason prompts
export const ReasonModal = ({ open, onClose, onConfirm, title, placeholder, loading }) => {
  const [reason, setReason] = React.useState('');
  React.useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <div className="space-y-5 flex-1">
        <Textarea
          label="Reason *"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placeholder || 'Enter reason...'}
          rows={3}
        />
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (reason.trim()) onConfirm(reason.trim()); }} loading={loading} disabled={!reason.trim()}>Confirm</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── ThemeToggle ─────────────────────────────────────────────────────────────
export const ThemeToggle = ({ theme, onToggle }) => (
  <button onClick={onToggle} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
    {theme === 'dark' ? (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
    ) : (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>
    )}
  </button>
);
